import { useMemo } from 'react';
import { useGetStatsQuery, useListRecordsQuery } from '@platform/state/api';
import { openClosedOf } from '@platform/core/lib/recordCounts';
import type { IRecordTypeDefinition } from '@platform/core/types';
import type { IModuleAdvice } from './IModuleAdvice';
import type { KpiKey } from './KpiKey';
import { adviceOf, openBucketsOf, tiedBottlenecksOf } from './moduleAdvice';
import { useOverviewKpis } from './useOverviewKpis';
import { useRiskCounts } from './useRiskCounts';

/**
 * Показники огляду (useOverviewKpis), статистика `by=status` без фільтра та поради модуля за правилами прототипу v4 (adviceOf) — однакові
 * для всіх модулів, реєстрів теж; реєстр ризиків додає поради про відкриті критичні й прийняті високі ризики (useRiskCounts). Коли два
 * етапи мають рівно по половині відкритої роботи, «вузьке місце» — етап найменшого за номером запису серед них (порядок масиву прототипу).
 */
export function useModuleAdvice(ws: string, rt: IRecordTypeDefinition): IModuleAdvice {
  const kpis = useOverviewKpis(ws, rt);
  const risk = useRiskCounts(ws, rt);
  const { data: stats } = useGetStatsQuery({ ws, rt: rt.key, by: 'status', params: {} });
  const open = openClosedOf(stats, rt.statuses).open;
  const openByStatus = useMemo(() => openBucketsOf(stats?.buckets ?? [], rt.statuses), [stats, rt.statuses]);
  const tied = tiedBottlenecksOf(open, openByStatus);
  const filter = `status:in:${tied.map(encodeURIComponent).join(',')}`;
  const first = useListRecordsQuery({ ws, rt: rt.key, params: { filter, sort: 'code', pageSize: 10, facets: false } }, { skip: tied.length < 2 });
  const firstStatus = tied.length < 2 ? undefined : first.data?.items[0]?.status;
  const ready = Boolean(stats) && kpis.every((k) => !k.loading) && risk.ready && (tied.length < 2 || !first.isFetching);
  const advice = useMemo(() => {
    const value = (key: KpiKey) => kpis.find((k) => k.key === key)?.value;
    return adviceOf({
      overdue: value('overdue'), unassigned: value('unassigned'), stale: value('stale'), open, openByStatus,
      critical: risk.critical, accepted: risk.accepted, firstStatus,
    });
  }, [kpis, open, openByStatus, risk.critical, risk.accepted, firstStatus]);
  return { kpis, stats, advice: ready ? advice : [], ready };
}
